/**
 * work-hours-field.tsx — Ô "Giờ làm & chế độ" của một nhân viên AI (DÙNG CHUNG: màn nhân viên AI, luồng tạo mới).
 *
 * 2 mẫu nhanh ("Trong giờ gợi ý, ngoài giờ tự động" / "Tự động 24/7") + "Tuỳ chỉnh giờ & chế độ" + "Một chế độ cả ngày".
 * Hiển thị theo GIỜ LÀM VIỆC (08:00–18:00); máy chủ lưu khung ngoài giờ — chuyển đổi ở work-hours-lib.ts.
 *
 * Props: `value` (các trường lịch của bot), `onChange(next)` (toàn bộ giá trị mới), `disabled`.
 */
import { Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { AiMode } from '@/hooks/use-ai'
import { AI_MODE_LABEL } from '@/lib/ai-khung'
import {
  HOURS, SCHEDULE_KINDS, applyScheduleKind, asDaytime, asNighttime, formatHour, scheduleFromWorkHours, scheduleKindOf,
  scheduleModeNow, scheduleSummary, workHoursFromSchedule, workHoursProblem, type ScheduleValue,
} from './work-hours-lib'

const MODES: AiMode[] = ['manual', 'suggest', 'auto']
const MODE_HINT_KEY: Record<AiMode, string> = { manual: 'workHours.hint.manual', suggest: 'workHours.hint.suggest', auto: 'workHours.hint.auto' }
const WINDOW_KEY = { 'in-hours': 'workHours.window.inHours', 'after-hours': 'workHours.window.afterHours', 'all-day': 'workHours.window.allDay' } as const

export function WorkHoursField({ value, onChange, disabled }: {
  value: ScheduleValue
  onChange: (next: ScheduleValue) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('aiComp')
  const kind = scheduleKindOf(value)
  const hours = workHoursFromSchedule(value.scheduleStartHour, value.scheduleEndHour)
  const now = scheduleModeNow(value)
  const problem = workHoursProblem(value)
  const setHours = (from: number, to: number) => onChange({ ...value, ...scheduleFromWorkHours(from, to) })

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('workHours.howToSet')}>
        {SCHEDULE_KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            role="radio"
            aria-checked={kind === k.id}
            disabled={disabled}
            onClick={() => onChange(applyScheduleKind(k.id, value))}
            className={cn(
              'rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-60',
              kind === k.id ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'hover:bg-muted/50',
            )}
          >
            <span className="block text-sm font-medium">{k.label}</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">{k.hint}</span>
          </button>
        ))}
      </div>

      {(kind === 'custom' || kind === 'q4') && (
        <div className="grid gap-3 rounded-lg bg-muted/30 p-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">{t('workHours.hours')}</Label>
            <div className="flex items-center gap-2">
              <HourSelect value={hours.from} onChange={(h) => setHours(h, hours.to)} disabled={disabled} label={t('workHours.hoursFrom')} />
              <span className="text-sm text-muted-foreground">{t('workHours.to')}</span>
              <HourSelect value={hours.to} onChange={(h) => setHours(hours.from, h)} disabled={disabled} label={t('workHours.hoursTo')} />
            </div>
            {problem && <p className="text-xs text-destructive">{problem}</p>}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('workHours.inHours')}</Label>
            <Select value={asDaytime(value.scheduleDaytimeMode)} onValueChange={(v) => onChange({ ...value, scheduleEnabled: true, scheduleDaytimeMode: v })} disabled={disabled}>
              <SelectTrigger aria-label={t('workHours.modeInHours')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="suggest">{t('workHours.opt.daySuggest')}</SelectItem>
                <SelectItem value="manual">{t('workHours.opt.dayManual')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('workHours.afterHours')}</Label>
            <Select value={asNighttime(value.scheduleNighttimeMode)} onValueChange={(v) => onChange({ ...value, scheduleEnabled: true, scheduleNighttimeMode: v })} disabled={disabled}>
              <SelectTrigger aria-label={t('workHours.modeAfterHours')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">{t('workHours.opt.nightAuto')}</SelectItem>
                <SelectItem value="suggest">{t('workHours.opt.nightSuggest')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {kind === 'single' && (
        <div className="space-y-1 rounded-lg bg-muted/30 p-3">
          <Label className="text-xs">{t('workHours.modeAllDay')}</Label>
          <div className="flex flex-wrap gap-1.5">
            {MODES.map((m) => (
              <button
                key={m}
                type="button"
                disabled={disabled}
                onClick={() => onChange(m === 'auto' ? applyScheduleKind('auto247', value) : { ...value, scheduleEnabled: false, defaultMode: m })}
                title={t(MODE_HINT_KEY[m])}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-50',
                  value.defaultMode === m ? 'border-primary bg-primary/10 font-medium text-primary' : 'hover:bg-muted',
                )}
              >
                {AI_MODE_LABEL[m]}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{t(MODE_HINT_KEY[value.defaultMode])}.</p>
        </div>
      )}

      <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        <span>{scheduleSummary(value)}.</span>
        <span>{t('workHours.nowLead', { window: t(WINDOW_KEY[now.window]) })}: <b className="text-foreground">{AI_MODE_LABEL[now.mode]}</b>.</span>
      </p>
    </div>
  )
}

function HourSelect({ value, onChange, disabled, label }: { value: number; onChange: (h: number) => void; disabled?: boolean; label: string }) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))} disabled={disabled}>
      <SelectTrigger className="w-28" aria-label={label}><SelectValue /></SelectTrigger>
      <SelectContent>
        {HOURS.map((h) => <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>)}
      </SelectContent>
    </Select>
  )
}
